import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaEye, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

const API = process.env.REACT_APP_API_BASE_URL;

interface User {
  id: number;
  name: string;
  email: string;
  role_id: number;
  created_at: string;
}

export default function UsersAdminPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState("");
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const token = Cookies.get("token");

      const res = await axios.get(`${API}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page,
          role_id: roleFilter,
        },
      });

      setUsers(res.data.data);
      setTotalPages(res.data.last_page);
    } catch (error) {
      Swal.fire("Error", "Gagal mengambil data user", "error");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter]);

  const openEdit = (user: User) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;

    const token = Cookies.get("token");

    try {
      await axios.put(
        `${API}/api/users/${selectedUser.id}`,
        {
          name: selectedUser.name,
          email: selectedUser.email,
          role_id: selectedUser.role_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      Swal.fire("Berhasil", "User berhasil diupdate", "success");

      setShowModal(false);
      fetchUsers();
    } catch {
      Swal.fire("Error", "Gagal update user", "error");
    }
  };

  const handleDelete = async (id: number) => {
    const token = Cookies.get("token");

    const confirm = await Swal.fire({
      title: "Hapus User?",
      text: "Data tidak bisa dikembalikan",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya Hapus",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API}/api/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire("Berhasil", "User berhasil dihapus", "success");
      fetchUsers();
    } catch {
      Swal.fire("Error", "Gagal menghapus user", "error");
    }
  };

  return (
    <div className="p-6">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Users Management</h1>

        <button onClick={() => navigate("/dashboard/admin/tambah")} className="bg-primary text-white px-4 py-2 rounded-lg">
          + Tambah Admin
        </button>
      </div>

      {/* FILTER */}
      <div className="mb-4">
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border p-2 rounded">
          <option value="">Semua Role</option>
          <option value="1">Admin</option>
          <option value="2">User</option>
        </select>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Nama</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Role</th>
              <th className="p-3 text-left">Tanggal</th>
              <th className="p-3 text-center">Aksi</th>
            </tr>
          </thead>

          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t">
                <td className="p-3">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.role_id === 1 ? "Admin" : "User"}</td>
                <td className="p-3">{new Date(u.created_at).toLocaleString()}</td>

                <td className="p-3 flex justify-center gap-4">
                  {/* EDIT */}
                  <button onClick={() => openEdit(u)} className="text-blue-500 hover:text-blue-700">
                    <FaEye />
                  </button>

                  {/* DELETE */}
                  <button onClick={() => handleDelete(u.id)} className="text-red-500 hover:text-red-700">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-end gap-2 mt-4">
        {[...Array(totalPages)].map((_, i) => (
          <button key={i} onClick={() => setPage(i + 1)} className={`px-3 py-1 rounded ${page === i + 1 ? "bg-primary text-white" : "bg-gray-200"}`}>
            {i + 1}
          </button>
        ))}
      </div>

      {/* MODAL EDIT */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-[400px]">
            <h2 className="text-xl font-bold mb-4">Edit User</h2>

            <div className="space-y-3">
              <input type="text" value={selectedUser.name} onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })} className="w-full border p-2 rounded" placeholder="Nama" />

              <input type="email" value={selectedUser.email} onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })} className="w-full border p-2 rounded" placeholder="Email" />

              <select
                value={selectedUser.role_id}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    role_id: Number(e.target.value),
                  })
                }
                className="w-full border p-2 rounded">
                <option value={1}>Admin</option>
                <option value={2}>User</option>
              </select>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="w-full bg-gray-200 py-2 rounded">
                Batal
              </button>

              <button onClick={handleUpdate} className="w-full bg-primary text-white py-2 rounded">
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
